/**
 * ActiveUsersTable + PendingInvitesTable + Th sort header.
 * Phase 7 extract from pages/Users.jsx.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Trash2, Pencil, ArrowUp, ArrowDown, ArrowUpDown, UserCog,
    Power, PowerOff, ShieldCheck, AlertCircle, Send, Clock, LockOpen, Lock,
} from "lucide-react";
import { ROLE_LABELS } from "@/components/users/constants";
import { SsoProviderBadge } from "@/lib/sso-providers";

export function Th({ field, label, sortBy, sortDir, onClick, center = false }) {
    const active = sortBy === field;
    const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
        <th className={`px-4 py-3 eyebrow whitespace-nowrap ${center ? "text-center" : "text-left"}`}>
            <button type="button" onClick={() => onClick(field)}
                className={`inline-flex items-center gap-1 ${active ? "text-foreground" : "text-muted-foreground"} hover:text-foreground transition-colors`}
                data-testid={`users-sort-${field}`}>
                {label}
                <Icon className="h-3 w-3 opacity-60" />
            </button>
        </th>
    );
}

export function ActiveUsersTable({ filtered, me, canManage, canDelete, openEdit, toggleActive, remove, canImpersonate, onImpersonate, onUnlock, onLock, sortBy, sortDir, toggleSort }) {
    return (
        <div className="border border-border rounded-sm bg-card overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-border bg-secondary/30">
                        <Th field="name" label="Name" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                        <Th field="email" label="Email" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                        <Th field="org_name" label="Organization" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                        <Th field="tenant_name" label="Entity" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                        <Th field="role" label="Role" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                        <Th field="created_at" label="Activated on" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                        <Th field="last_login_at" label="Last login" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                        <Th field="sso_required" label="SSO" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} center />
                        <Th field="is_active" label="Status" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                        <th className="text-right px-4 py-3 eyebrow whitespace-nowrap">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.length === 0 ? (
                        <tr><td colSpan={10} className="text-center py-16 text-sm text-muted-foreground" data-testid="users-empty">
                            No users match your search.
                        </td></tr>
                    ) : filtered.map((u) => (
                        <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={`user-row-${u.id}`}>
                            <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-7 h-7 bg-foreground text-background font-mono text-[11px] font-semibold flex items-center justify-center rounded-sm">
                                        {(u.name || u.email).split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                    </div>
                                    <span className="font-medium">{u.name}</span>
                                </div>
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{u.email}</td>
                            <td className="px-4 py-3 text-xs" data-testid={`user-org-${u.id}`}>{u.org_name || "—"}</td>
                            <td className="px-4 py-3 text-xs" data-testid={`user-tenant-${u.id}`}>{u.tenant_name || "—"}</td>
                            <td className="px-4 py-3">
                                <Badge variant="outline" className="font-mono text-[10px]" data-testid={`user-role-badge-${u.id}`}>
                                    {ROLE_LABELS[u.role] || u.role}
                                </Badge>
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground" data-testid={`user-activated-${u.id}`}>
                                {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                                {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "—"}
                            </td>
                            <td className="px-4 py-3 text-center" data-testid={`user-sso-${u.id}`}>
                                {u.sso_required ? (
                                    <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-primary">
                                        <ShieldCheck className="h-3.5 w-3.5" />
                                        {u.sso_provider ? (
                                            <SsoProviderBadge
                                                provider={u.sso_provider}
                                                className="text-primary"
                                                testid={`user-sso-provider-${u.id}`}
                                            />
                                        ) : (
                                            <span>ON</span>
                                        )}
                                    </span>
                                ) : (
                                    <span className="text-[11px] font-mono text-muted-foreground">—</span>
                                )}
                            </td>
                            <td className="px-4 py-3">
                                <Badge variant="outline" className={`font-mono text-[10px] ${u.is_locked ? "border-primary text-primary" : (u.is_active ? "" : "text-muted-foreground")}`} data-testid={`user-status-${u.id}`}>
                                    {u.is_locked ? (
                                        <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> LOCKED</span>
                                    ) : (u.is_active ? "ACTIVE" : "DISABLED")}
                                </Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                    {/* iter-120 round-4 follow-up #14 — Always-visible
                                        Lock / Unlock toggle so admins can see the action
                                        without waiting for an actual brute-force lockout.
                                        SA/OA with `canManage` can lock anyone except
                                        themselves; locked users get an Unlock action. */}
                                    {canManage && u.id !== me?.id && (
                                        u.is_locked ? (
                                            <Button variant="ghost" size="sm"
                                                    className="h-7 w-7 p-0 text-primary hover:text-primary hover:bg-primary/10"
                                                    onClick={() => onUnlock && onUnlock(u)}
                                                    title="Unlock account"
                                                    data-testid={`user-unlock-${u.id}`}>
                                                <LockOpen className="h-3.5 w-3.5" />
                                            </Button>
                                        ) : (
                                            <Button variant="ghost" size="sm"
                                                    className="h-7 w-7 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10"
                                                    onClick={() => onLock && onLock(u)}
                                                    title="Lock account"
                                                    data-testid={`user-lock-${u.id}`}>
                                                <Lock className="h-3.5 w-3.5" />
                                            </Button>
                                        )
                                    )}
                                    {canImpersonate && canImpersonate(u) && (
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-primary hover:text-primary hover:bg-primary/10"
                                                onClick={() => onImpersonate(u)} title="Impersonate user" data-testid={`user-impersonate-${u.id}`}>
                                            <UserCog className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                    {canManage && (
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(u)} title="Edit user" data-testid={`user-edit-${u.id}`}>
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                    {canManage && u.id !== me?.id && (
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => toggleActive(u)}
                                                title={u.is_active ? "Deactivate user" : "Activate user"} data-testid={`user-toggle-active-${u.id}`}>
                                            {u.is_active ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5 text-primary" />}
                                        </Button>
                                    )}
                                    {canDelete && u.id !== me?.id && (
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => remove(u)} title="Delete user" data-testid={`user-delete-${u.id}`}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function PendingInvitesTable({ rows, canManage, onResend, onDelete }) {
    return (
        <div className="border border-border rounded-sm bg-card overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-border bg-secondary/30">
                        <th className="text-left px-4 py-3 eyebrow">Invitee</th>
                        <th className="text-left px-4 py-3 eyebrow">Email</th>
                        <th className="text-left px-4 py-3 eyebrow">Organization / Entity</th>
                        <th className="text-left px-4 py-3 eyebrow">Role</th>
                        <th className="text-left px-4 py-3 eyebrow">Created</th>
                        <th className="text-left px-4 py-3 eyebrow">SSO</th>
                        <th className="text-left px-4 py-3 eyebrow">Status</th>
                        <th className="text-right px-4 py-3 eyebrow">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-16 text-sm text-muted-foreground" data-testid="invites-empty">
                            No pending invitations.
                        </td></tr>
                    ) : rows.map((inv) => (
                        <tr key={inv.id} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={`pending-invite-${inv.id}`}>
                            <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-7 h-7 bg-foreground/10 text-foreground font-mono text-[10px] font-semibold flex items-center justify-center rounded-sm">
                                        {(inv.name || inv.email).split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                    </div>
                                    <span className="font-medium">{inv.name}</span>
                                </div>
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{inv.email}</td>
                            <td className="px-4 py-3 text-xs">
                                {inv.org_name || "—"}
                                <div className="font-mono text-[10px] text-muted-foreground mt-0.5">{inv.tenant_name || "—"}</div>
                            </td>
                            <td className="px-4 py-3">
                                <Badge variant="outline" className="font-mono text-[10px]">{ROLE_LABELS[inv.role] || inv.role}</Badge>
                            </td>
                            <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground whitespace-nowrap" data-testid={`invite-created-${inv.id}`}>
                                {inv.created_at ? new Date(inv.created_at).toLocaleString() : "—"}
                            </td>
                            <td className="px-4 py-3" data-testid={`invite-sso-${inv.id}`}>
                                {inv.sso_required ? (
                                    <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-primary">
                                        <ShieldCheck className="h-3.5 w-3.5" />
                                        {inv.sso_provider ? (
                                            <SsoProviderBadge
                                                provider={inv.sso_provider}
                                                className="text-primary"
                                                testid={`invite-sso-provider-${inv.id}`}
                                            />
                                        ) : (
                                            <span>SSO</span>
                                        )}
                                    </span>
                                ) : (
                                    <span className="text-[11px] font-mono text-muted-foreground">—</span>
                                )}
                            </td>
                            <td className="px-4 py-3">
                                {inv.expired ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-primary"><AlertCircle className="h-3 w-3" /> expired</span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-muted-foreground"><Clock className="h-3 w-3" /> expires {new Date(inv.expires_at).toLocaleDateString()}</span>
                                )}
                            </td>
                            <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                    {canManage && (
                                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={() => onResend(inv)} data-testid={`resend-invite-${inv.id}`}>
                                            <Send className="h-3 w-3 mr-1" /> Resend
                                        </Button>
                                    )}
                                    {canManage && (
                                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onDelete(inv)} title="Revoke invitation" data-testid={`delete-invite-${inv.id}`}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
